import { defineStore } from "pinia";
import { searchPosts } from "@/fetch";
import { months } from "./locales";
import { makeOption, processSettings } from "@/utils";

export interface Term {
  id: number;
  slug: string;
  name: string;
  order: number;
  children: Array<Term>;
}

interface WPTerm {
  id: number;
  slug: string;
  name: string;
  order: number;
  parent: number;
}

interface ItemTerm {
  slug: string;
  name: string;
}

export interface Year {
  year: number;
  months: Array<number>;
}

export interface Item {
  id: number;
  lang: string;
  title: string;
  date: string;
  link: string;
  terms: Record<string, Array<ItemTerm>>;
  search: Array<string>;
  html: string;
}

export interface Settings {
  showSearch: boolean;
  showSort: boolean;
  showMonth: boolean;
  sortBy: string;
  asc: boolean;
  filterRadio: boolean;
  masonry: boolean;
  columns: number;
  perPage: number;
}

export interface TaxonomyFilter {
  id: string;
  name: string;
  type: string;
  placeholder: string;
  root: string;
}

interface ItemStore {
  type: string;
  lang: string;
  total: number;
  page: number;
  show: number;
  settings: Settings;
  filter: Record<string, string[]>;
  terms: Record<string, Term[]>;
  year: number;
  month: number;
  months: Array<Year>;
  items: Array<Item>;
  historyInhibit: boolean;
  search: string;
  taxonomies: Array<TaxonomyFilter>;
}

export interface Option {
  value: string;
  text: string;
  level: number;
}

export const useItemStore = defineStore("items", {
  state: () =>
    ({
      type: "",
      lang: "",
      total: 0,
      page: 1,
      show: 6,
      settings: {
        showSearch: true,
        showSort: true,
        showMonth: false,
        sortBy: "date",
        asc: true,
        filterRadio: true,
        masonry: false,
        columns: 3,
        perPage: 6,
      } as Settings,
      year: 0,
      month: 0,
      filter: {},
      terms: {},
      months: [],
      items: [],
      historyInhibit: false,
      search: "",
      taxonomies: [],
    } as ItemStore),
  getters: {
    /**
     * Filters out items according to given criteria.
     * @returns {Array<Item>}
     */
    filterItems: function (state): Array<Item> {
      return state.items.filter((item) => {
        if (item.lang !== state.lang) {
          return false;
        }
        for (const taxonomy of Object.keys(state.terms)) {
          const terms = state.filter[taxonomy];
          if (terms !== undefined && terms.length > 0) {
            let hit = false;
            if (item.terms !== undefined) {
              const itemTerms = item.terms[taxonomy];
              if (itemTerms !== undefined) {
                for (const term of itemTerms) {
                  if (terms.includes(term.slug)) hit = true;
                }
              }
            }
            if (!hit) return false;
          }
        }

        if (state.search.length > 0) {
          return (
            item.title.toLowerCase().includes(state.search.toLowerCase()) ||
            item.search.includes(state.search.toLowerCase())
          );
        }
        if (state.year > 0) {
          const date = new Date(item.date);
          if (date.getFullYear() != state.year) {
            return false;
          }
          if (state.month > 0) {
            if (date.getMonth() + 1 != state.month) {
              return false;
            }
          }
        }
        return true;
      });
    },
    /**
     * Sort and limit the filtered list.
     * @returns {Array<Item>}
     */
    getItems: function (state): Array<Item> {
      return this.filterItems
        .sort((a, b) => {
          if (state.settings.sortBy == "title") {
            if (a.title === b.title) return 0;
            if (a.title > b.title) return state.settings.asc ? 1 : -1;
            return state.settings.asc ? -1 : 1;
          } else {
            if (a.date === b.date) return 0;
            if (a.date > b.date) return -1;
            return 1;
          }
        })
        .slice(0, state.show);
    },
    getTaxonomies: function (state): Array<string> {
      const keys = [];
      for (const key of Object.keys(state.terms)) {
        keys.push(key);
      }
      return keys;
    },
    /**
     * Check if there is more items to show.
     * @returns {boolean}
     */
    hasMore: function (state): boolean {
      return state.total > state.show && this.filterItems.length >= state.show;
    },
    getMonth: function (state): string {
      return months(state.lang)[state.month];
    },
    getMonths: function (state): Array<number> {
      if (state.year == 0) {
        return [];
      }
      for (const year of state.months) {
        if (state.year == year.year) {
          return year.months;
        }
      }
      return [];
    },
    getFilter:
      (state) =>
      (taxonomy: string): Array<string> => {
        const filter = state.filter[taxonomy];
        if (filter !== undefined) {
          return filter;
        }
        return [];
      },
    getTerms:
      (state) =>
      (taxonomy: string): Array<Term> => {
        const terms = state.terms[taxonomy];
        if (terms !== undefined) {
          return terms;
        }
        return [];
      },
    getOptions:
      (state) =>
      (taxonomy: string, children = false, root = ""): Array<Option> => {
        const terms = state.terms[taxonomy];
        if (terms !== undefined) {
          return makeOption(terms, children, root);
        }
        return [];
      },
    getJoinedTerms: (state) => {
      return Object.keys(state.terms)
        .map((key) => state.terms[key].map((term) => term.slug).join(","))
        .join("/");
    },
    getJoinedFilters: (state) => {
      return Object.keys(state.filter)
        .map((key) => state.filter[key].join(","))
        .join("/");
    },
  },
  actions: {
    setFilter(taxonomy: string, slug: string, radio = true) {
      const filter = this.filter[taxonomy] ?? [];
      if (radio) {
        // Replace all old values with the new value.
        filter.length = 0;
        if (slug) {
          filter.push(slug);
        }
      } else {
        if (filter.includes(slug)) {
          // Remove the item.
          for (let i = 0; i < filter.length; i++) {
            if (filter[i] === slug) {
              filter.splice(i, 1);
              break;
            }
          }
        } else {
          // Add the item.
          filter.push(slug);
        }
      }
      this.filter[taxonomy] = filter;

      /*
       * Set the filter as hash path.
       */
      // Set this to inhibit update from history change.
      this.historyInhibit = true;
      // Make a string of all the active filters.
      const hashSource = [];
      for (const taxonomy of Object.keys(this.terms)) {
        const filter = this.filter[taxonomy] ?? [];
        if (filter.length) {
          hashSource.push(`${taxonomy}:${filter.join(",")}`);
        }
      }

      if (hashSource.length > 0) {
        window.location.hash = hashSource.join("/");
      } else {
        // Remove the # path if no filters selected.
        const uri = window.location.toString();
        if (uri.indexOf("#") > 0) {
          window.history.replaceState({}, document.title, uri.substring(0, uri.indexOf("#")));
        }
      }

      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    addItem(item: Item) {
      let found = false;
      this.items.forEach((existingItem) => {
        if (existingItem.id === item.id) {
          found = true;
          if (item.search.length > 0) {
            existingItem.search.push(...item.search);
          }
        }
      });
      if (!found) {
        // Add item if not in store.
        this.items.push(item);
      }
    },
    addTerms(terms: Array<WPTerm>, taxonomy: string) {
      const data = [] as Array<Term>;

      for (const term of terms) {
        if (term.parent == 0) {
          data.push({ ...term, children: [] } as Term);
        }
      }
      for (const term of terms) {
        if (term.parent > 0) {
          data.forEach((c) => {
            if (c.id == term.parent) {
              c.children.push({ ...term, children: [] } as Term);
            }
          });
        }
      }
      this.terms[taxonomy] = data;
    },
    addTaxonomy(id: string, name: string, type: string, placeholder: string, root: string) {
      const filter = { id, name, type, placeholder, root } as TaxonomyFilter;
      this.taxonomies.push(filter);
    },
    setTaxonomyRoot(slug: string, root: string) {
      for (const taxonomy of this.taxonomies) {
        if (taxonomy.name === slug) {
          taxonomy.root = root;
        }
      }
    },
    toggleSort() {
      this.settings.asc = !this.settings.asc;
      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    setYear(year: number) {
      if (this.year != year) {
        this.month = 0;
      }
      this.year = year;
      searchPosts();
    },
    setMonth(month: number) {
      this.month = month;
      searchPosts();
    },
    showMore() {
      // Increase number of items to show by one "page".
      this.show += this.settings.perPage;
      // Backend uses twice the perPage value as page size.
      const haveGotten = this.page * this.settings.perPage * 2;
      // Check that there is always an extra page of items gotten. But cap value at total items.
      const toGet =
        this.show + this.settings.perPage > this.total
          ? this.total
          : this.show + this.settings.perPage;
      if (this.page > 0 && haveGotten < toGet) {
        // If we have not gotten enough items, get more.
        this.page++;
        searchPosts();
      }
    },
    resetShow() {
      this.page = 1;
      this.show = this.settings.perPage;
    },
  },
});

/**
 * Gets stored values from session storage.
 * This is only safe to run on an empty store.
 */
export const initializeSessionStorage = (type: string, lang: string): void => {
  const itemStore = useItemStore();
  if (type && lang) {
    itemStore.type = type;
    itemStore.lang = lang;
    const key = itemStore.type + "_" + itemStore.lang;
    const json = sessionStorage.getItem(key);
    if (json !== null) {
      const data = JSON.parse(json);

      // Restore items.
      for (const item of data.items) {
        itemStore.addItem(item);
      }
      for (const taxonomy of Object.keys(data.terms)) {
        const terms = data.terms[taxonomy];
        itemStore.addTerms(terms, taxonomy);
      }
      itemStore.months = data.months as Array<Year>;
      itemStore.settings = data.settings as Settings;

      processSettings(itemStore.settings);
    }
  }
};

/**
 * Saves some store values to session storage.
 * Includes store subscriber, so should only be run once.
 */
export const sessionStorageListener = (): void => {
  const itemStore = useItemStore();
  if (itemStore.type && itemStore.lang) {
    const key = itemStore.type + "_" + itemStore.lang;
    itemStore.$subscribe(() => {
      const json = JSON.stringify({
        terms: itemStore.terms,
        months: itemStore.months,
        items: itemStore.items,
        settings: itemStore.settings,
      });
      sessionStorage.setItem(key, json);
    });
  }
};

export const loadMoreHandler = () => {
  const button = document.getElementById("dynamic-archive-load-more");
  const shown = document.getElementById("dynamic-archive-shown");
  const total = document.getElementById("dynamic-archive-total");

  const itemStore = useItemStore();
  if (button !== null) {
    button.addEventListener("click", () => {
      itemStore.showMore();
    });
  }
  itemStore.$subscribe(() => {
    if (button !== null) {
      if (itemStore.hasMore) {
        button.classList.remove("hidden");
      } else {
        button.classList.add("hidden");
      }
    }
    if (shown !== null) {
      shown.innerText = itemStore.getItems.length.toString();
    }
    if (total !== null) {
      total.innerText = itemStore.total.toString();
    }
  });
};
